import { toast } from 'sonner';
import { syncAllData } from './sync-service';
import { eventBus, SYNC_EVENTS } from '../utils';

 // Helper to check if user is authenticated
 // Uses dynamic import to avoid circular dependency issues with React
 const isAuthenticated = async (): Promise<boolean> => {
   try {
     const { supabase } = await import('@/integrations/supabase/client');
     const { data: { session } } = await supabase.auth.getSession();
     return !!session?.user;
   } catch {
     return false;
   }
 };

type SyncStatus = {
  inProgress: boolean;
  lastSyncTime: number | null;
  progress: number;
  error: Error | null;
};

type SyncQueueItem = {
  id: string;
  priority: number;
  execute: () => Promise<void>;
};

class BackgroundSyncService {
  private status: SyncStatus = {
    inProgress: false,
    lastSyncTime: null,
    progress: 0,
    error: null
  };
  
  private syncQueue: SyncQueueItem[] = [];
  private syncTimeout: ReturnType<typeof setTimeout> | null = null;
  private syncAttempts = 0;
  private maxSyncAttempts = 3;
  private listeners: Array<(status: SyncStatus) => void> = [];
  
  constructor() {
    // Listen for online status changes
    window.addEventListener('online', () => this.scheduleSync('priority'));
  }
  
  public getStatus(): SyncStatus {
    return { ...this.status };
  }
  
  public addListener(callback: (status: SyncStatus) => void): () => void {
    this.listeners.push(callback);
    // Immediately notify with current status
    callback(this.status);
    
    // Return function to remove listener
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }
  
  private notifyListeners(): void {
    this.listeners.forEach(callback => callback(this.status));
  }
  
  public scheduleSync(type: 'normal' | 'priority' = 'normal', delay: number = type === 'priority' ? 500 : 5000): void {
    if (this.syncTimeout) {
      clearTimeout(this.syncTimeout);
    }
    
    // Reset sync attempts for new sync schedule
    if (!this.status.inProgress) {
      this.syncAttempts = 0;
    }
    
    const syncId = Date.now().toString();
    
    // Add to queue with priority
    this.syncQueue.push({
      id: syncId,
      priority: type === 'priority' ? 1 : 0,
      execute: async () => {
        try {
          // Don't try to sync if we're offline
          if (!navigator.onLine) {
            console.log('Device is offline, skipping sync');
            return;
          }
           
           // Don't try to sync if not authenticated
           if (!await isAuthenticated()) {
             console.log('User not authenticated, skipping sync');
             return;
           }
          
          this.status = {
            ...this.status,
            inProgress: true,
            progress: 10,
            error: null
          };
          this.notifyListeners();
          
          console.log('Starting background sync...');
          
          // Execute the actual sync operation
          await syncAllData();
          
          // Update status on success
          this.status = {
            inProgress: false,
            lastSyncTime: Date.now(),
            progress: 100,
            error: null
          };
          
          // Reset sync attempts on success
          this.syncAttempts = 0;
          
          // Notify listeners of successful sync
          eventBus.dispatch(SYNC_EVENTS.SYNC_COMPLETED, { timestamp: this.status.lastSyncTime });
          
          console.log('Background sync completed successfully');
        } catch (error: any) {
          console.error('Background sync error:', error);
          this.syncAttempts += 1;
          
          this.status = {
            ...this.status,
            inProgress: false,
            error: error instanceof Error ? error : new Error(String(error))
          };
          
          // Only show error toast when attempts exceed limit
          if (this.syncAttempts >= this.maxSyncAttempts) {
            toast.error('Sync failed. Some changes may not be saved online.');
            
            // Dispatch error event for components to handle
            eventBus.dispatch(SYNC_EVENTS.SYNC_ERROR, { error });
          } else {
            // Schedule another attempt
            console.log(`Scheduling retry attempt ${this.syncAttempts}...`);
            this.scheduleSync('normal', 10000 * this.syncAttempts); // Back off exponentially
          }
        } finally {
          this.notifyListeners();
        }
      }
    });
    
    // Sort queue by priority (higher priority first)
    this.syncQueue.sort((a, b) => b.priority - a.priority);
    
    // Schedule execution if not already in progress
    if (!this.status.inProgress) {
      this.syncTimeout = setTimeout(() => this.processSyncQueue(), delay);
    }
  }
  
  private async processSyncQueue(): Promise<void> {
    if (this.syncQueue.length === 0 || this.status.inProgress) {
      return;
    }
    
    // Take the highest priority item
    const item = this.syncQueue.shift();
    if (item) {
      await item.execute();
    }
    
    // If there are more items, process the next one
    if (this.syncQueue.length > 0) {
      this.syncTimeout = setTimeout(() => this.processSyncQueue(), 1000);
    }
  }
  
  // For manual sync triggering with progress updates
  public async syncNow(): Promise<void> {
    if (this.status.inProgress) {
      return;
    }
    
    try {
       // Check authentication before syncing
       if (!await isAuthenticated()) {
         console.log('syncNow: User not authenticated, skipping sync');
         return;
       }
 
      this.status = {
        ...this.status,
        inProgress: true,
        progress: 0,
        error: null
      };
      this.notifyListeners();
      
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        if (this.status.inProgress && this.status.progress < 90) {
          this.status = {
            ...this.status,
            progress: this.status.progress + 10
          };
          this.notifyListeners();
        }
      }, 300);
      
      await syncAllData();
      
      clearInterval(progressInterval);
      
      this.status = {
        inProgress: false,
        lastSyncTime: Date.now(),
        progress: 100,
        error: null
      };
      
      // Notify of successful sync
      eventBus.dispatch(SYNC_EVENTS.SYNC_COMPLETED, { timestamp: this.status.lastSyncTime });
    } catch (error: any) {
      this.status = {
        ...this.status,
        inProgress: false,
        progress: 0,
        error: error instanceof Error ? error : new Error(String(error))
      };
      
      toast.error('Sync failed. Changes saved locally.');
      eventBus.dispatch(SYNC_EVENTS.SYNC_ERROR, { error });
    } finally {
      this.notifyListeners();
    }
  }
}

// Create a singleton instance
export const backgroundSync = new BackgroundSyncService();
